import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import { startCheckout as beginCheckout } from "../utils/startCheckout.js";
import {
  PRICING_INTRO,
  PRICING_GROWTH_NOTE,
  PRICING_TIERS,
  PRICING_PORTABILITY_NOTE,
  PRICING_TRIAL_NOTE,
  PRICING_MEMBER_PRICE_SUMMARY,
  getMemberDisplayPrice,
  getTeamsContactHref
} from "../constants/pricing.js";

const PricingTierCard = ({
  tier,
  isHighlighted,
  cta,
  ctaDisabled,
  onCtaClick,
  priceOverride,
  periodOverride
}) => (
  <article className={`pricing-tier${isHighlighted ? " pricing-tier--featured" : ""}`}>
    {isHighlighted ? <p className="pricing-tier__badge">Most growth</p> : null}
    <h2 className="pricing-tier__name">{tier.name}</h2>
    <p className="pricing-tier__tagline">{tier.tagline}</p>
    <p className="pricing-tier__price">
      <span className="pricing-tier__amount">{priceOverride ?? tier.price}</span>
      <span className="pricing-tier__period">{periodOverride ?? tier.period}</span>
    </p>
    <h3 className="pricing-tier__list-title">What you get</h3>
    <ul className="pricing-tier__features">
      {tier.features.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
    <h3 className="pricing-tier__list-title">How it strengthens response discipline</h3>
    <ul className="pricing-tier__helps">
      {tier.helps.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
    {cta ? (
      <button
        type="button"
        className={`primary-button pricing-tier__cta${isHighlighted ? " pricing-tier__cta--featured" : ""}`}
        onClick={onCtaClick}
        disabled={ctaDisabled}
      >
        {cta}
      </button>
    ) : null}
  </article>
);

const Pricing = ({ verifiedEmail, isPaidSubscriber }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const memberDisplay = getMemberDisplayPrice();

  const handleMemberCheckout = async () => {
    if (isPaidSubscriber) return;
    if (!verifiedEmail) {
      navigate("/login");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      await beginCheckout(verifiedEmail);
    } catch (err) {
      console.error(err);
      setError("Unable to start checkout right now. Please try again.");
      setIsLoading(false);
    }
  };

  const freeTier = PRICING_TIERS.free;
  const memberTier = PRICING_TIERS.member;

  return (
    <PageContainer className="about-page pricing-page">
      <h1 className="about-page__title">Pricing</h1>
      <p className="about-page__lead">{PRICING_INTRO}</p>
      <p className="pricing-page__note">{PRICING_GROWTH_NOTE}</p>
      <p className="pricing-page__price-summary">
        Member: {PRICING_MEMBER_PRICE_SUMMARY}. {PRICING_TRIAL_NOTE}
      </p>

      <div className="pricing-page__grid">
        <PricingTierCard
          tier={freeTier}
          isHighlighted={false}
          cta={freeTier.cta}
          onCtaClick={() => navigate(freeTier.ctaTo)}
        />
        <PricingTierCard
          tier={memberTier}
          isHighlighted
          cta={isPaidSubscriber ? "You're a member" : memberTier.cta}
          ctaDisabled={isPaidSubscriber || isLoading}
          onCtaClick={handleMemberCheckout}
          priceOverride={memberDisplay.amount}
          periodOverride={memberDisplay.period}
        />
      </div>

      <p className="pricing-page__portability">{PRICING_PORTABILITY_NOTE}</p>

      {error ? <p className="login-error pricing-page__error">{error}</p> : null}

      {isPaidSubscriber ? (
        <p className="pricing-page__member-note">
          You have an active membership. Manage invoices on the{" "}
          <Link to="/billing">billing page</Link>.
        </p>
      ) : (
        <p className="pricing-page__footer-note">
          Member plan: {PRICING_MEMBER_PRICE_SUMMARY}. {PRICING_TRIAL_NOTE}{" "}
          Cancel anytime from your billing settings.
        </p>
      )}

      <p className="pricing-page__teams">
        <strong>For teams</strong> —{" "}
        <a href={getTeamsContactHref()}>talk to us</a> about decision-quality tools for leaders.
      </p>

      <section className="about-section pricing-page__faq-link">
        <h2 className="about-section__heading">Questions?</h2>
        <p className="about-section__p">
          See the <Link to="/faq">FAQ</Link> for privacy, who Ahavibes is for, and how structured reflection works.
        </p>
      </section>
    </PageContainer>
  );
};

export default Pricing;
